// @flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useHistory, useParams, useLocation} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import {Typography, IconButton, useMediaQuery} from '@material-ui/core';
import {
  ArrowBack,
  DeleteOutlined,
  // VisibilityOffOutlined,
} from '@material-ui/icons';
import queryString from 'query-string';

import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import DeleteConfirmationModal from './DeleteConfirmationModal/DeleteConfirmationModal';
import SnackBar from '../../../elements/SnackBar/SnackBar';

import type {
  Product as ProductType,
  Store,
  User,
  Currency,
  Category,
} from '../../../entities';

import {usePage} from '../../page/utils';
import ProductForm from './ProductForm';
import {useIsFeatureInTier} from '../../../utils/component';
import {ProductFormProvider} from '../context/ProductForm';

import type {ProductRequest, ProductVariantsRequest} from '../api';
import type {ApiError} from '../../../http/error';
import {FormProvider, useForm} from 'react-hook-form';
import ProductHeaderTabs from './ProductHeaderTabs/ProductHeaderTabs';
import {useStoreBusinessModel} from '../../store/utils';

type Props = {|
  store?: Store,
  user?: User,
  currency?: Currency,
  categories?: Category[],
  fetchProduct: ({productId: string}) => void,
  product?: ProductType,
  isFetching?: boolean,
  isSaving: boolean,
  resetProductState: () => void,
  resetProductSettings: () => void,
  createProduct: ({
    productRequest: ProductRequest,
    productVariantsRequest?: ProductVariantsRequest,
  }) => void,
  createProductSuccess?: string | boolean,
  editProduct: ({
    productRequest: ProductRequest,
    productVariantsRequest?: ProductVariantsRequest,
  }) => void,
  editProductSuccess?: boolean,
  deleteProduct: ({productRequest: ProductRequest}) => void,
  deleteProductSuccess?: boolean,
  productOperationError?: ApiError | string,
  fetchProductError?: string,
  createFinalSettingProductSuccess?: boolean,
  defaultImages?: string[],
  defaultDescription?: string,
  isInstagramUpload?: boolean,
  instagramUploadEarlyExit?: () => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F9FB',
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  headerWrapper: {
    maxWidth: 1300,
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 0, 4),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 0, 2),
      },
    },
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  deleteButton: {
    color: colors.error,
  },
  alert: {
    maxWidth: 1300,
    margin: theme.spacing(1, 4, 0, 4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(1, 2, 0, 2),
    },
  },
  bottomBar: {
    zIndex: 1200, // same as sidebar
    position: 'fixed',
    bottom: 0,
    width: 'calc(100% - 264px)',
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      width: '100%',
    },
  },
  bottomBarContent: {
    maxWidth: 1300,
    padding: theme.spacing(2, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bottomBarText: {
    fontWeight: 600,
  },
  bottomBarButton: {
    height: 48,
    fontSize: '1.125rem',
  },
  mobileBottomButton: {
    zIndex: 1,
    position: 'fixed',
    bottom: 64,
    height: 48,
    margin: theme.spacing(2),
    width: 'calc(100% - 32px)',
    fontSize: '1.125rem',
  },
  productTabs: {
    width: '100%',
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
  },
}));

export default usePage<Props>(Product, 'Product');

function getPageTitle(
  businessModel: string,
  isDuplicating: boolean,
  product?: ProductType
) {
  if (isDuplicating) {
    return `Duplicar ${businessModel === 'SERVICE' ? 'Servicio' : 'Producto'}`;
  }
  if (product) {
    return `Editar ${businessModel === 'SERVICE' ? 'Servicio' : 'Producto'}`;
  }
  return `Nuevo ${businessModel === 'SERVICE' ? 'Servicio' : 'Producto'}`;
}

function Product(props: Props) {
  const {
    fetchProduct,
    resetProductState,
    resetProductSettings,
    product,
    // isSaving,
    // createProduct,
    // createProductSuccess,
    // editProduct,
    // editProductSuccess,
    deleteProduct,
    // deleteProductSuccess,
    productOperationError,
    fetchProductError,
    defaultImages,
    defaultDescription,
    isInstagramUpload,
    instagramUploadEarlyExit,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const {productId} = useParams();
  const location = useLocation();
  const queryObject = queryString.parse(window.location.search);

  const isDuplicating = location.pathname.includes('/duplicate');

  const [tabSelected, setTabSelected] = useState<string>('general');
  const mobile = useMediaQuery('(max-width:600px)');

  const businessModel = useStoreBusinessModel();

  const mainForm = useForm();

  useEffect(() => {
    if (productId && productId !== 'new') fetchProduct({productId});
  }, [fetchProduct, productId]);

  const userHasProductCreationFeature = useIsFeatureInTier('PRODUCT_CREATION');
  const userHasProductEditFeature = useIsFeatureInTier('PRODUCT_EDIT');
  const userHasProductDeleteFeature = useIsFeatureInTier('PRODUCT_DELETE');

  useEffect(() => {
    if (
      (productId &&
        (productId === 'new'
          ? !userHasProductCreationFeature
          : !userHasProductEditFeature)) ||
      fetchProductError
    )
      history.replace('/products');
  }, [
    fetchProductError,
    history,
    productId,
    userHasProductCreationFeature,
    userHasProductEditFeature,
  ]);

  useEffect(() => {
    return () => {
      resetProductSettings();
    };
  }, [resetProductSettings, resetProductState]);

  const productErrorMessage = useMemo(() => {
    if (productOperationError && productOperationError.status === 403)
      return 'Se llegó al número máximo de productos permitidos';

    if (productOperationError && productOperationError.status === 400) {
      if (productOperationError.error) {
        if (productOperationError.error.message === 'Invalid bulk prices')
          return 'Precios al por mayor inválidos';
        if (productOperationError.error.message === 'Duplicated sku')
          return 'No puedes tener skus duplicados en tu tienda';
        if (
          productOperationError.error.message ===
          'The offer price must be less than the regular price'
        )
          return 'El precio de oferta debe ser menor al precio regular';
      }
      return 'Error de edición de producto';
    }

    if (typeof productOperationError === 'string') {
      return productOperationError;
    } else
      return (
        productOperationError &&
        productOperationError.error &&
        productOperationError.error.message
      );
  }, [productOperationError]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const handleDeleteModalOpen = () => setDeleteModalOpen(true);
  const handleDeleteModalClose = () => setDeleteModalOpen(false);
  const handleDeleteClick = () => {
    if (product) deleteProduct({productRequest: {id: product.id}});
  };

  const savedCallback = useCallback(() => {
    if (queryObject.fromOnboarding) {
      resetProductState();
      history.push(`/home`);
    } else history.push(`/products`);
  }, [history, queryObject.fromOnboarding, resetProductState]);

  const pageTitle = getPageTitle(businessModel, isDuplicating, product);

  return (
    <div className={classes.root}>
      <ProductFormProvider>
        <FormProvider {...mainForm}>
          <SnackBar mode="error" message={productErrorMessage} />
          <AlertDueDateTier />
          <div className={classes.headerWrapper}>
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              <Typography className={classes.title}>
                <IconButton
                  className={classes.backButton}
                  onClick={() => history.goBack()}
                >
                  <ArrowBack />
                </IconButton>
                {pageTitle}
              </Typography>
              {product && userHasProductDeleteFeature && (
                <CustomButton
                  className={classes.deleteButton}
                  endIcon={<DeleteOutlined />}
                  onClick={handleDeleteModalOpen}
                >
                  {mobile
                    ? null
                    : `Eliminar ${
                        businessModel === 'SERVICE' ? 'Servicio' : 'Producto'
                      }`}
                </CustomButton>
              )}
            </div>
            <div className={classes.productTabs}>
              <ProductHeaderTabs
                onTabSelected={setTabSelected}
                tabSelected={tabSelected}
              />
            </div>
          </div>

          <ProductForm
            product={product}
            isDuplicating={isDuplicating}
            savedCallback={savedCallback}
            tabSelected={tabSelected}
            defaultImages={defaultImages}
            defaultDescription={defaultDescription}
            isInstagramUpload={isInstagramUpload}
            instagramUploadEarlyExit={instagramUploadEarlyExit}
          />

          {product && (
            <DeleteConfirmationModal
              title="¿Seguro que quieres eliminar este producto?"
              content="Si eliminas el producto no lo podrás recuperar."
              open={deleteModalOpen}
              handleClose={handleDeleteModalClose}
              handleDeleteClick={handleDeleteClick}
            />
          )}
        </FormProvider>
      </ProductFormProvider>
    </div>
  );
}

Product.propTypes = {
  store: PropTypes.object,
  user: PropTypes.object,
  currency: PropTypes.object,
  categories: PropTypes.array,
  fetchProduct: PropTypes.func.isRequired,
  product: PropTypes.object,
  isFetching: PropTypes.bool,
  isSaving: PropTypes.bool,
  resetProductState: PropTypes.func.isRequired,
  resetProductSettings: PropTypes.func.isRequired,
  createProduct: PropTypes.func.isRequired,
  createProductSuccess: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  editProduct: PropTypes.func.isRequired,
  editProductSuccess: PropTypes.bool,
  deleteProduct: PropTypes.func.isRequired,
  deleteProductSuccess: PropTypes.bool,
  productOperationError: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  createFinalSettingProductSuccess: PropTypes.bool,
  fetchProductError: PropTypes.string,
};
